/**
 * Jot — JotPsych's "little guy", built from the 8 rounded squares of the
 * logomark. One square is the head; the other 7 are his moods.
 *
 * Framework-free imperative engine: one rAF loop, fixed 1/120s damped-spring
 * physics, pose-lerped eyes, a state machine with randomized expression pools,
 * and satellite-square choreography. Mount into an empty <svg> with
 * `createJotEngine(svg)`; drive it through the returned controller.
 */

export type JotStateName =
  | 'sleeping'
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'working'
  | 'happy'
  | 'excited'
  | 'curious'
  | 'surprised'
  | 'suspicious'
  | 'sad'
  | 'celebrate'
  | 'logo'
  | 'swarm'
  | 'writing'
  | 'typing'
  | 'approved'
  | 'paid'
  | 'auditing'
  | 'loading'
  | 'redacted'
  | 'shy'
  | 'love'
  | 'dizzy'
  | 'bouncing';

export interface JotEngine {
  setState: (next: JotStateName) => void;
  getState: () => JotStateName;
  /** Full 360° spin (stacks if called repeatedly). */
  spin: () => void;
  /** Confetti burst of the seven satellite squares. */
  burst: () => void;
  /** Little squash-and-hop acknowledgement (the "you clicked me" reaction). */
  hop: () => void;
  /** Global pointer position in client coords; eyes follow it. */
  setPointer: (x: number, y: number) => void;
  /** Stop scheduling frames while hidden or resting; resume without a time jump. */
  setPaused: (paused: boolean) => void;
  destroy: () => void;
}

const MID = '#1C1E85';
const PINK = '#FD96C9';
export const JOT_VIEWBOX = '0 0 320 340';
const CX = 160;
const CY = 172; // character center in viewBox
const HEAD_SIZE = 150; // head rect edge
const U = 49.5; // logo grid unit

/* ---------- spring physics ---------- */
const DT = 1 / 120;
interface Spring {
  x: number;
  v: number;
  t: number;
}
function spring(x: number): Spring {
  return { x, v: 0, t: x };
}
function step(s: Spring, omega: number, zeta: number): void {
  s.v += (-2 * zeta * omega * s.v - omega * omega * (s.x - s.t)) * DT;
  s.x += s.v * DT;
  if (!Number.isFinite(s.x) || !Number.isFinite(s.v)) {
    s.x = s.t;
    s.v = 0;
  }
}
const rand = (a: number, b: number) => a + Math.random() * (b - a);
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/* ---------- eye poses ----------
   Params are for the LEFT eye. mir 1 = params mirror across the face;
   mir 0 = both eyes identical (lets "happy" be two upward arches). */
interface Pose {
  dx: number;
  dy: number;
  len: number;
  rot: number;
  bow: number;
  w: number;
  mir: number;
}
const POSES: Record<string, Pose> = {
  neutral: { dx: 0, dy: 0, len: 30, rot: 0, bow: 0, w: 13, mir: 1 },
  soft: { dx: 0, dy: 1, len: 24, rot: 0, bow: 0, w: 13, mir: 1 },
  glanceL: { dx: -7, dy: -1, len: 28, rot: 0, bow: 0, w: 13, mir: 1 },
  glanceR: { dx: 7, dy: -1, len: 28, rot: 0, bow: 0, w: 13, mir: 1 },
  up: { dx: 0, dy: -6, len: 28, rot: 0, bow: 0, w: 13, mir: 1 },
  upL: { dx: -6, dy: -6, len: 26, rot: 0, bow: 0, w: 13, mir: 1 },
  upR: { dx: 6, dy: -6, len: 26, rot: 0, bow: 0, w: 13, mir: 1 },
  wide: { dx: 0, dy: -2, len: 36, rot: 0, bow: 0, w: 15, mir: 1 },
  big: { dx: 0, dy: -3, len: 40, rot: 0, bow: 0, w: 17, mir: 1 },
  happy: { dx: 0, dy: -2, len: 24, rot: 90, bow: -8.5, w: 12, mir: 0 },
  squint: { dx: 0, dy: 2, len: 13, rot: 0, bow: 0, w: 13, mir: 1 },
  lid: { dx: 2, dy: -3, len: 13, rot: 0, bow: 0, w: 12, mir: 1 },
  sad: { dx: 2, dy: 3, len: 22, rot: 16, bow: 0, w: 12, mir: 1 },
  closed: { dx: 0, dy: 3, len: 15, rot: 90, bow: 0, w: 9, mir: 1 },
  dot: { dx: 0, dy: 0, len: 5, rot: 0, bow: 0, w: 10, mir: 1 },
  down: { dx: 0, dy: 8, len: 24, rot: 0, bow: 0, w: 13, mir: 1 },
  dream: { dx: 0, dy: 4, len: 12, rot: 90, bow: 0, w: 9, mir: 1 },
};
function lerpPose(a: Pose, b: Pose, t: number): Pose {
  return {
    dx: lerp(a.dx, b.dx, t),
    dy: lerp(a.dy, b.dy, t),
    len: lerp(a.len, b.len, t),
    rot: lerp(a.rot, b.rot, t),
    bow: lerp(a.bow, b.bow, t),
    w: lerp(a.w, b.w, t),
    mir: lerp(a.mir, b.mir, t),
  };
}

/* ---------- state machine ---------- */
interface StateDef {
  pool: string[];
  hold: [number, number];
  mode: string;
  gaze: boolean;
  blink: boolean;
  logoish?: boolean;
  wobble?: boolean;
  tilt?: number;
  scan?: boolean;
  droop?: boolean;
  dim?: boolean;
  hop?: 'fast' | 'big';
}
const STATES: Record<JotStateName, StateDef> = {
  sleeping: { pool: ['closed'], hold: [6000, 9000], mode: 'zzz', gaze: false, blink: false },
  idle: {
    pool: ['neutral', 'soft', 'glanceL', 'glanceR', 'up'],
    hold: [2600, 6000],
    mode: 'hidden',
    gaze: true,
    blink: true,
  },
  listening: {
    pool: ['wide', 'neutral', 'up'],
    hold: [2200, 4200],
    mode: 'eq',
    gaze: true,
    blink: true,
  },
  thinking: {
    pool: ['upL', 'upR', 'squint', 'up'],
    hold: [1600, 3200],
    mode: 'dots',
    gaze: false,
    blink: true,
  },
  working: {
    pool: ['neutral', 'squint', 'glanceL', 'glanceR'],
    hold: [1400, 2800],
    mode: 'sqorbit',
    gaze: false,
    blink: true,
  },
  happy: { pool: ['happy'], hold: [2500, 4500], mode: 'hidden', gaze: true, blink: false },
  excited: {
    pool: ['happy', 'big'],
    hold: [900, 1700],
    mode: 'hidden',
    gaze: true,
    blink: false,
    hop: 'fast',
  },
  curious: {
    pool: ['wide', 'upL', 'upR', 'glanceL'],
    hold: [1500, 2800],
    mode: 'hidden',
    gaze: true,
    blink: true,
    tilt: 7,
  },
  surprised: { pool: ['big', 'wide'], hold: [2000, 3500], mode: 'alert', gaze: true, blink: false },
  suspicious: {
    pool: ['lid', 'squint'],
    hold: [2400, 4200],
    mode: 'hidden',
    gaze: true,
    blink: true,
    scan: true,
  },
  sad: {
    pool: ['sad', 'closed'],
    hold: [3500, 6000],
    mode: 'hidden',
    gaze: false,
    blink: true,
    droop: true,
  },
  celebrate: {
    pool: ['happy'],
    hold: [1400, 2600],
    mode: 'party',
    gaze: false,
    blink: false,
    hop: 'fast',
  },
  logo: {
    pool: ['closed'],
    hold: [9e9, 9e9],
    mode: 'logo',
    gaze: false,
    blink: false,
    logoish: true,
  },
  swarm: {
    pool: ['wide', 'neutral', 'glanceL', 'glanceR'],
    hold: [1600, 3200],
    mode: 'swarm',
    gaze: true,
    blink: true,
  },
  writing: {
    pool: ['glanceR', 'down', 'squint'],
    hold: [1600, 3000],
    mode: 'note',
    gaze: false,
    blink: true,
  },
  typing: {
    pool: ['down', 'squint', 'glanceL', 'glanceR'],
    hold: [1500, 2800],
    mode: 'type',
    gaze: false,
    blink: true,
  },
  approved: { pool: ['happy', 'big'], hold: [2000, 3800], mode: 'check', gaze: true, blink: false },
  paid: {
    pool: ['happy', 'up', 'wide'],
    hold: [1600, 3000],
    mode: 'coin',
    gaze: false,
    blink: true,
  },
  auditing: { pool: ['squint', 'lid'], hold: [2000, 3600], mode: 'scan', gaze: false, blink: true },
  loading: { pool: ['down', 'soft'], hold: [2200, 4000], mode: 'bar', gaze: false, blink: true },
  redacted: { pool: ['closed'], hold: [9e9, 9e9], mode: 'redact', gaze: false, blink: false },
  shy: {
    pool: ['soft', 'glanceL', 'down'],
    hold: [2000, 4000],
    mode: 'blush',
    gaze: true,
    blink: true,
    tilt: 6,
  },
  love: { pool: ['happy'], hold: [3000, 5000], mode: 'heart', gaze: true, blink: false },
  dizzy: {
    pool: ['glanceL', 'up', 'glanceR', 'down'],
    hold: [260, 500],
    mode: 'stars',
    gaze: false,
    blink: false,
    wobble: true,
  },
  bouncing: {
    pool: ['happy', 'wide'],
    hold: [1800, 3200],
    mode: 'hidden',
    gaze: false,
    blink: false,
    hop: 'big',
  },
};

/* ---------- logo geometry ---------- */
const LOGO_SCALE = 0.62;
const HEAD_SLOT = { c: 1, r: 3 }; // a MIDNIGHT slot — Jot keeps his own color
const SAT_SLOTS = [
  { c: 2, r: 0, color: PINK },
  { c: 1, r: 1, color: MID },
  { c: 0, r: 2, color: PINK },
  { c: 2, r: 2, color: PINK },
  { c: 3, r: 3, color: MID },
  { c: 2, r: 4, color: PINK },
  { c: 1, r: 5, color: MID },
];
function slotXY(c: number, r: number): [number, number] {
  const w = 4 * U * LOGO_SCALE;
  const h = 6 * U * LOGO_SCALE;
  return [CX - w / 2 + (c + 0.5) * U * LOGO_SCALE, CY - h / 2 + (r + 0.5) * U * LOGO_SCALE];
}

const CHECK: Array<[number, number]> = [
  [-34, 2],
  [-22, 14],
  [-10, 26],
  [3, 12],
  [16, -2],
  [29, -16],
];

const NS = 'http://www.w3.org/2000/svg';

interface Sat {
  el: SVGGElement;
  rect: SVGRectElement;
  eyes: SVGPathElement;
  dollar: SVGTextElement;
  slot: { c: number; r: number; color: string };
  i: number;
  fillNow: string;
  x: Spring;
  y: Spring;
  s: Spring;
  sy: Spring;
  r: Spring;
  op: Spring;
  inFront: boolean;
  wx: number;
  wy: number;
  wanderAt: number;
  hopAt: number;
  bl: Spring;
  blAt: number;
  bx: number;
  by: number;
  bvx: number;
  bvy: number;
  brot: number;
  bvr: number;
  ballistic: boolean;
}

/* ---------- eye rendering ---------- */
const EYE_AX = 27;
const EYE_AY = -17; // anchors relative to head center (upper third reads cuter)
function eyePath(
  cx: number,
  cy: number,
  len: number,
  rotDeg: number,
  bow: number,
  blink: number,
): string {
  const closed = POSES.closed;
  const b = clamp(blink, 0, 1);
  len = lerp(closed.len, len, b);
  rotDeg = lerp(90, rotDeg, b); // 90° = horizontal line when fully blinked
  bow = lerp(0, bow, b);
  const a = (rotDeg * Math.PI) / 180;
  const dx = Math.sin(a);
  const dy = -Math.cos(a);
  const px = -dy;
  const py = dx;
  const h = Math.max(len / 2, 0.5);
  const x1 = cx - dx * h;
  const y1 = cy - dy * h;
  const x2 = cx + dx * h;
  const y2 = cy + dy * h;
  const mx = cx + px * bow;
  const my = cy + py * bow;
  return `M${x1.toFixed(2)} ${y1.toFixed(2)}Q${mx.toFixed(2)} ${my.toFixed(2)} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

export function createJotEngine(
  svg: SVGSVGElement,
  { maxFps = 60 }: { maxFps?: number } = {},
): JotEngine {
  const ownerDocument = svg.ownerDocument;
  const ownerWindow = ownerDocument.defaultView ?? window;
  svg.setAttribute('viewBox', JOT_VIEWBOX);
  svg.style.overflow = 'visible';

  const mk = <K extends keyof SVGElementTagNameMap>(tag: K): SVGElementTagNameMap[K] =>
    ownerDocument.createElementNS(NS, tag);

  const BACK = mk('g');
  const GUY = mk('g');
  const FRONT = mk('g');
  const HEAD = mk('rect');
  HEAD.setAttribute('x', String(-HEAD_SIZE / 2));
  HEAD.setAttribute('y', String(-HEAD_SIZE / 2));
  HEAD.setAttribute('width', String(HEAD_SIZE));
  HEAD.setAttribute('height', String(HEAD_SIZE));
  HEAD.setAttribute('rx', '30');
  HEAD.setAttribute('fill', MID);
  const EYES_G = mk('g');
  const EYE_L = mk('path');
  const EYE_R = mk('path');
  for (const e of [EYE_L, EYE_R]) {
    e.setAttribute('fill', 'none');
    e.setAttribute('stroke', '#ffffff');
    e.setAttribute('stroke-linecap', 'round');
    EYES_G.appendChild(e);
  }
  GUY.appendChild(HEAD);
  GUY.appendChild(EYES_G);
  svg.appendChild(BACK);
  svg.appendChild(GUY);
  svg.appendChild(FRONT);

  const sats: Sat[] = SAT_SLOTS.map((slot, i) => {
    const el = mk('g');
    const rect = mk('rect');
    rect.setAttribute('x', String(-U / 2));
    rect.setAttribute('y', String(-U / 2));
    rect.setAttribute('width', String(U));
    rect.setAttribute('height', String(U));
    rect.setAttribute('rx', '10.4');
    rect.setAttribute('fill', slot.color);
    rect.style.transition = 'fill .3s';
    el.appendChild(rect);
    const eyes = mk('path');
    eyes.setAttribute('d', 'M-8.5 -8L-8.5 3M8.5 -8L8.5 3');
    eyes.setAttribute('stroke', '#ffffff');
    eyes.setAttribute('stroke-width', '7.5');
    eyes.setAttribute('stroke-linecap', 'round');
    eyes.setAttribute('fill', 'none');
    eyes.style.opacity = '0';
    eyes.style.transition = 'opacity .35s';
    el.appendChild(eyes);
    const dollar = mk('text');
    dollar.textContent = '$';
    dollar.setAttribute('text-anchor', 'middle');
    dollar.setAttribute('dominant-baseline', 'central');
    dollar.setAttribute('font-family', 'Archivo, Hanken Grotesk, Inter, sans-serif');
    dollar.setAttribute('font-weight', '700');
    dollar.setAttribute('font-size', '38');
    dollar.setAttribute('fill', MID);
    dollar.setAttribute('y', '2');
    dollar.style.opacity = '0';
    dollar.style.transition = 'opacity .35s';
    el.appendChild(dollar);
    BACK.appendChild(el);
    return {
      el,
      rect,
      eyes,
      dollar,
      slot,
      i,
      fillNow: slot.color,
      x: spring(CX),
      y: spring(CY),
      s: spring(0.001),
      sy: spring(0.001),
      r: spring(0),
      op: spring(0),
      inFront: false,
      wx: CX,
      wy: CY,
      wanderAt: 0,
      hopAt: 0,
      bl: spring(1),
      blAt: rand(1500, 4500),
      bx: 0,
      by: 0,
      bvx: 0,
      bvy: 0,
      brot: 0,
      bvr: 0,
      ballistic: false,
    };
  });

  /* ---------- runtime state ---------- */
  let state: JotStateName = 'idle';
  let stateTime = 0;
  let now = 0;

  let poseFrom = POSES.neutral;
  let poseTo = POSES.neutral;
  const poseT = spring(1);
  let nextPoseAt = 0;
  let lastPoseName = 'neutral';

  const blinkS = spring(1);
  let nextBlinkAt = 1800;
  let winkEye = -1;

  const gaze = { x: 0, y: 0, tx: 0, ty: 0 };
  let pointer: { x: number; y: number } | null = null;

  const swayX = spring(0);
  const swayY = spring(0);
  const swayRot = spring(0);
  const scaleX = spring(1);
  const scaleY = spring(1);
  const headScale = spring(1);
  const eyeOp = spring(1);
  const guyOp = spring(1);
  const spinDeg = spring(0);
  let wanderAt = 0;

  let hopPhase = 0;
  let partyAt = 0;
  let typePrev = 0;

  let rafId = 0;
  let frameTimer = 0;
  let paused = false;
  const frameInterval = 1000 / Math.max(1, Math.min(60, maxFps));
  let destroyed = false;
  const timeouts = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number) => {
    const id = setTimeout(() => {
      timeouts.delete(id);
      fn();
    }, ms);
    timeouts.add(id);
  };

  function currentPose(): Pose {
    const t = clamp(poseT.x, 0, 1.35);
    return lerpPose(poseFrom, poseTo, t);
  }
  function advancePose(): void {
    const def = STATES[state];
    const pool = def.pool;
    let name = pool[Math.floor(Math.random() * pool.length)];
    if (pool.length > 1 && name === lastPoseName)
      name = pool[(pool.indexOf(name) + 1) % pool.length];
    lastPoseName = name;
    poseFrom = currentPose();
    poseTo = POSES[name];
    poseT.x = 0;
    poseT.v = 0;
    poseT.t = 1;
    nextPoseAt = now + rand(def.hold[0], def.hold[1]);
  }

  function setState(next: JotStateName): void {
    if (!STATES[next]) return;
    const prev = state;
    state = next;
    stateTime = 0;
    const def = STATES[next];
    nextPoseAt = now; // advance immediately next frame
    if (def.logoish) {
      eyeOp.t = 0;
      headScale.t = (U / HEAD_SIZE) * LOGO_SCALE;
    } else {
      eyeOp.t = def.dim ? 0.45 : 1;
      headScale.t = next === 'swarm' ? 0.78 : 1;
    }
    guyOp.t = 1;
    if (next === 'celebrate') partyAt = now + 60;
    if (next === 'approved' || next === 'paid') scaleY.v += 3; // little proud bounce on entry
    if (next === 'surprised') {
      scaleY.v -= 3.5;
      scaleX.v += 2.2; // recoil kick
    }
    if (prev === 'sleeping' && next !== 'sleeping') scaleY.v += 4; // wake stretch
  }

  /* ---------- satellite choreography ---------- */
  function satTargets(def: StateDef, t: number): void {
    const mode = def.mode;
    sats.forEach((s, i) => {
      s.ballistic = mode === 'party' && s.ballistic;
      let tx = CX;
      let ty = CY;
      let ts = 0.001;
      let tsy: number | null = null;
      let tr = 0;
      let top = 0;
      let front = false;
      let fill = s.slot.color;
      if (mode === 'logo') {
        const [x, y] = slotXY(s.slot.c, s.slot.r);
        tx = x;
        ty = y;
        ts = LOGO_SCALE;
        top = 1;
      } else if (mode === 'dots' && i < 3) {
        // three thought-squares bobbing overhead
        const bob = Math.sin(t / 340 + i * 1.1) * 7;
        tx = CX + (i - 1) * 36;
        ty = CY - 118 + bob - (i === 1 ? 6 : 0);
        ts = 0.3 + 0.05 * Math.sin(t / 260 + i * 2.1);
        tr = Math.sin(t / 500 + i) * 14;
        top = 1;
      } else if (mode === 'sqorbit' && i < 6) {
        // squares marching axis-aligned along a rounded-SQUARE path
        const a = t / 1400 + (i * Math.PI) / 3;
        const k = 0.72;
        const sc = Math.cos(a);
        const sn = Math.sin(a);
        tx = CX + 124 * Math.sign(sc) * Math.pow(Math.abs(sc), k);
        ty = CY + 48 * Math.sign(sn) * Math.pow(Math.abs(sn), k) - 6;
        ts = 0.26;
        top = 1;
        front = sn > 0;
      } else if (mode === 'swarm') {
        // the logo breaks into mini-Jots that roam, hop and look around
        if (t >= s.wanderAt) {
          let n = 0;
          do {
            s.wx = rand(46, 274);
            s.wy = rand(58, 292);
          } while (Math.hypot(s.wx - CX, s.wy - CY) < 102 && ++n < 8);
          s.wanderAt = t + rand(1400, 3200);
        }
        if (t >= s.hopAt) {
          s.y.v -= rand(150, 260);
          s.hopAt = t + rand(1200, 3400);
        }
        tx = s.wx;
        ty = s.wy;
        ts = 0.3 + (i % 3) * 0.025;
        tr = clamp((s.wx - s.x.x) * 0.25, -10, 10); // lean into travel
        top = 1;
        front = false; // duck behind the big guy (midnight-on-midnight reads as ghost eyes)
      } else if (mode === 'eq' && i < 3) {
        // listening: three equalizer bars dancing above his head
        tx = CX + (i - 1) * 26;
        ty = CY - 128;
        ts = 0.13;
        tsy = 0.18 + 0.36 * Math.abs(Math.sin(t / 170 + i * 0.9));
        top = 1;
      } else if (mode === 'note') {
        // writing, abstractly: a ribbon of word-squares streams out of him
        const cyc = (t / 2600 + i / 7) % 1;
        tx = CX + 52 + cyc * 118;
        ty = CY + 2 + Math.sin(cyc * 8.5 + i * 1.3) * 12 - cyc * 28;
        ts = 0.2 * (1 - 0.5 * cyc);
        tr = Math.sin(cyc * 12 + i) * 24;
        top = cyc < 0.1 ? cyc / 0.1 : 1 - Math.pow(cyc, 2.2);
        if (cyc < 0.04) {
          s.x.x = tx;
          s.y.x = ty; // respawn at his side
        }
      } else if (mode === 'type' && i < 6) {
        // typewriter: a cursor bar steps across his face leaving letter-marks
        const P = 2600;
        const cyc = (stateTime % P) / P;
        const steps = 8;
        const prog = Math.min(cyc / 0.82, 1);
        const k = Math.floor(prog * steps);
        const cursorX = CX - 56 + (k / steps) * 112;
        if (i === 0) {
          tx = cyc > 0.82 ? CX - 56 : cursorX;
          ty = CY - 2;
          ts = 0.11;
          tsy = 1.05;
          top = cyc > 0.82 ? 0.3 : 0.95;
          front = true;
        } else {
          const j = i - 1; // five marks along the line
          const mx = CX - 46 + j * 23;
          tx = mx;
          ty = CY + 4;
          ts = 0.1;
          tsy = 0.1;
          top = cursorX > mx + 8 && cyc <= 0.85 ? 1 : 0;
          front = true;
        }
        fill = PINK; // marks must read against his midnight face
        s.x.x = tx;
        s.y.x = ty; // typewriters SNAP — no spring glide between steps
      } else if (mode === 'check' && i < 6) {
        // squares pop in one by one to form a checkmark: note approved
        const pop = clamp((stateTime - 200 - i * 110) / 240, 0, 1);
        tx = CX + 2 + CHECK[i][0];
        ty = CY - 128 + CHECK[i][1];
        ts = 0.21 * pop;
        top = pop;
      } else if (mode === 'coin' && i === 0) {
        // JotBill payday: a square coin with a "$" bobs above his head
        tx = CX;
        ty = CY - 126 + Math.sin(t / 420) * 6;
        ts = 0.55;
        tr = Math.sin(t / 560) * 9;
        top = 1;
      } else if (mode === 'scan' && i === 0) {
        // JotAudit: a pink scan-bar sweeps over him, checking every line
        const ph = (t / 2400) % 1;
        const tri = ph < 0.5 ? ph * 2 : 2 - 2 * ph;
        tx = CX;
        ty = CY - 84 + tri * 168;
        ts = 3.1;
        tsy = 0.11;
        top = 0.9;
        front = true;
      } else if (mode === 'bar' && i < 5) {
        // a progress bar of squares filling beneath him
        const f = ((t / 2800) % 1) * 5.6;
        tx = CX - 56 + i * 28;
        ty = CY + 116;
        ts = i < f ? 0.2 : 0.07;
        top = 1;
      } else if (mode === 'redact' && i === 0) {
        // privacy mode: a bar slides over his eyes
        tx = CX;
        ty = CY - 16;
        ts = 2.7;
        tsy = 0.55;
        top = 1;
        front = true;
      } else if (mode === 'blush' && (i === 0 || i === 3)) {
        const side = i === 0 ? -1 : 1;
        tx = CX + side * 44;
        ty = CY + 15;
        ts = 0.18 + 0.014 * Math.sin(t / 320 + i);
        top = 1;
        front = true;
      } else if (mode === 'heart') {
        // all seven squares turn pink and march along a heart curve around him
        const a = t / 550 + (i * Math.PI * 2) / 7;
        tx = CX + 6.9 * 16 * Math.pow(Math.sin(a), 3);
        ty =
          CY -
          8 -
          6.1 * (13 * Math.cos(a) - 5 * Math.cos(2 * a) - 2 * Math.cos(3 * a) - Math.cos(4 * a));
        ts = 0.17;
        top = 1;
        front = ty > CY + 20;
        fill = PINK;
      } else if (mode === 'stars' && i < 3) {
        const a = t / 380 + i * 2.09;
        tx = CX + 62 * Math.cos(a);
        ty = CY - 94 + 20 * Math.sin(a);
        ts = 0.115;
        tr = (t / 4 + i * 40) % 360;
        top = 1;
        front = Math.sin(a) > 0;
      } else if (mode === 'alert' && i === 0) {
        const k = Math.min(t / 260, 1);
        tx = CX;
        ty = CY - 128 - Math.sin(k * Math.PI) * 14;
        ts = 0.3;
        tr = Math.sin(t / 160) * 10;
        top = 1;
      } else if (mode === 'zzz' && i < 3) {
        const cyc = (t / 1400 + i * 0.33) % 1;
        tx = CX + 74 + cyc * 34 + Math.sin(cyc * 9 + i) * 6;
        ty = CY - 78 - cyc * 74;
        ts = 0.17 - cyc * 0.1;
        tr = 18 + cyc * 40;
        top = cyc < 0.12 ? cyc / 0.12 : 1 - (cyc - 0.12) / 0.88;
        if (cyc < 0.03) {
          s.x.x = tx;
          s.y.x = ty; // snap the loop at wrap
        }
      }
      if (s.fillNow !== fill) {
        s.rect.setAttribute('fill', fill);
        s.fillNow = fill;
      }
      if (s.ballistic) return; // party integrates its own physics
      s.x.t = tx;
      s.y.t = ty;
      s.s.t = ts;
      s.sy.t = tsy == null ? ts : tsy;
      s.r.t = tr;
      s.op.t = top;
      if (front !== s.inFront) {
        (front ? FRONT : BACK).appendChild(s.el);
        s.inFront = front;
      }
    });
  }

  function burst(): void {
    sats.forEach(s => {
      s.ballistic = true;
      s.bx = CX + rand(-10, 10);
      s.by = CY - 20;
      const a = rand(-Math.PI * 0.85, -Math.PI * 0.15);
      const sp = rand(260, 430);
      s.bvx = Math.cos(a) * sp;
      s.bvy = Math.sin(a) * sp - 60;
      s.brot = rand(0, 360);
      s.bvr = rand(-420, 420);
      s.s.x = rand(0.16, 0.3);
      s.sy.x = s.s.x;
      s.op.x = 1;
      if (!s.inFront) {
        FRONT.appendChild(s.el);
        s.inFront = true;
      }
    });
    scaleY.v += 5; // anticipation squash
  }
  function stepBallistics(): void {
    sats.forEach(s => {
      if (!s.ballistic) return;
      s.bvy += 620 * DT;
      s.bx += s.bvx * DT;
      s.by += s.bvy * DT;
      s.brot += s.bvr * DT;
      if (s.by > CY + 210) {
        s.ballistic = false;
        s.x.x = s.bx;
        s.y.x = s.by;
        s.r.x = s.brot;
        s.op.x = 0;
        s.s.x = 0.001;
      } else {
        s.x.x = s.bx;
        s.y.x = s.by;
        s.r.x = s.brot;
      }
    });
  }

  /* ---------- main loop (fixed timestep) ---------- */
  let last = performance.now();
  let acc = 0;
  function scheduleFrame(): void {
    if (destroyed || paused) return;
    // Do not wake at the display's refresh rate just to discard frames.
    if (maxFps < 60) {
      frameTimer = ownerWindow.setTimeout(
        () => {
          rafId = ownerWindow.requestAnimationFrame(frame);
        },
        Math.max(0, frameInterval - (performance.now() - last) - 8),
      );
    } else rafId = ownerWindow.requestAnimationFrame(frame);
  }
  function frame(t: number): void {
    if (destroyed || paused) return;
    const dtMs = Math.min(t - last, 80);
    last = t;
    now = t;
    stateTime += dtMs;
    acc += dtMs / 1000;

    const def = STATES[state];

    if (now >= nextPoseAt) advancePose();
    if (def.blink && now >= nextBlinkAt) {
      blinkS.t = 0;
      winkEye = Math.random() < 0.12 ? (Math.random() < 0.5 ? 0 : 1) : -1;
      nextBlinkAt = now + rand(2400, 5200);
      later(() => {
        blinkS.t = 1;
      }, 110);
    }
    if (state === 'celebrate' && now >= partyAt) {
      burst();
      partyAt = now + rand(1900, 2600);
    }
    if (state === 'swarm')
      for (const s of sats)
        if (now >= s.blAt) {
          s.bl.t = 0;
          later(() => {
            s.bl.t = 1;
          }, 110);
          s.blAt = now + rand(2400, 6500);
        }
    if (!def.gaze) {
      gaze.tx = 0;
      gaze.ty = 0;
    } else if (pointer) {
      // eyes track the page cursor; normalized by viewport so the whole
      // screen maps onto his (small) range of eye motion
      const r = svg.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      gaze.tx = clamp((pointer.x - cx) / (ownerWindow.innerWidth * 0.55), -1, 1) * 15;
      gaze.ty = clamp((pointer.y - cy) / (ownerWindow.innerHeight * 0.55), -1, 1) * 10;
    }
    if (now >= wanderAt) {
      swayX.t = state === 'sleeping' ? 0 : rand(-5, 5);
      swayRot.t = def.scan
        ? rand(-8, 8)
        : def.tilt
          ? (Math.random() < 0.5 ? -1 : 1) * def.tilt
          : rand(-2.5, 2.5);
      wanderAt = now + rand(2000, 4600);
    }
    if (def.wobble) swayRot.t = Math.sin(now / 240) * 9;
    if (state === 'typing') {
      const cyc = (stateTime % 2600) / 2600;
      if (cyc < typePrev) swayX.v -= 28; // ding! carriage-return nudge
      typePrev = cyc;
    }
    if (def.droop) {
      swayY.t = 10;
      swayRot.t = -4;
    } else if (def.dim) {
      swayY.t = 16;
    } else if (state === 'sleeping') {
      swayY.t = 8;
      swayRot.t = 6;
    } else if (!def.hop) swayY.t = 0;

    let hopY = 0;
    if (def.hop) {
      hopPhase += dtMs / (def.hop === 'big' ? 620 : 420);
      const p = hopPhase % 1;
      const amp = def.hop === 'big' ? 46 : 16;
      hopY = -amp * 4 * p * (1 - p);
      const impact = p > 0.92 || p < 0.08 ? 1 : 0;
      scaleY.t = 1 - 0.16 * impact + (p > 0.3 && p < 0.7 ? 0.06 : 0);
      scaleX.t = 1 + 0.14 * impact - (p > 0.3 && p < 0.7 ? 0.04 : 0);
    } else {
      scaleX.t = 1;
      scaleY.t = 1;
      hopPhase = 0;
    }

    while (acc >= DT) {
      step(poseT, 11, 0.72);
      step(blinkS, 34, 0.9);
      step(swayX, 5, 0.8);
      step(swayY, 5, 0.8);
      step(swayRot, 5, 0.75);
      step(scaleX, 14, 0.6);
      step(scaleY, 14, 0.6);
      step(headScale, 9, 0.8);
      step(eyeOp, 10, 1);
      step(guyOp, 10, 1);
      step(spinDeg, 7, 0.62);
      const satW = def.mode === 'swarm' ? 3.6 : 9; // floatier drift while roaming
      sats.forEach(s => {
        step(s.bl, 30, 0.9);
        if (!s.ballistic) {
          step(s.x, satW, 0.74);
          step(s.y, satW, 0.74);
          step(s.s, 10, 0.8);
          step(s.sy, 10, 0.8);
          step(s.r, 8, 0.8);
          step(s.op, 12, 1);
        }
      });
      stepBallistics();
      acc -= DT;
    }
    const g = 1 - Math.exp(-dtMs / 90);
    gaze.x += (gaze.tx - gaze.x) * g;
    gaze.y += (gaze.ty - gaze.y) * g;

    const breathe = state === 'sleeping' ? Math.sin(t / 900) * 0.02 : Math.sin(t / 1400) * 0.008;
    let gx = CX + swayX.x + gaze.x * 0.35;
    let gy = CY + swayY.x + hopY;
    if (def.logoish) {
      const [hx, hy] = slotXY(HEAD_SLOT.c, HEAD_SLOT.r);
      gx = hx;
      gy = hy;
    }
    const hs = headScale.x;
    GUY.setAttribute(
      'transform',
      `translate(${gx.toFixed(2)} ${gy.toFixed(2)}) rotate(${(swayRot.x + spinDeg.x).toFixed(2)}) scale(${(scaleX.x * hs).toFixed(4)} ${((scaleY.x + breathe) * hs).toFixed(4)})`,
    );
    GUY.style.opacity = def.dim ? (0.5 + 0.12 * Math.sin(t / 800)).toFixed(3) : guyOp.x.toFixed(3);

    const p = currentPose();
    EYES_G.style.opacity = clamp(eyeOp.x, 0, 1).toFixed(3);
    if (eyeOp.x > 0.01) {
      for (let e = 0; e < 2; e++) {
        const side = e === 0 ? -1 : 1;
        const blink = winkEye === -1 || winkEye === e ? blinkS.x : 1;
        const ex = side * EYE_AX + p.dx + gaze.x * 0.65;
        const ey = EYE_AY + p.dy + gaze.y * 0.7;
        const el = e === 0 ? EYE_L : EYE_R;
        const mf = p.mir * side + (1 - p.mir); // mirror factor: mir→±1, unmirrored→1
        el.setAttribute('d', eyePath(ex, ey, p.len, p.rot * mf, p.bow * mf, blink));
        el.setAttribute('stroke-width', lerp(POSES.closed.w, p.w, clamp(blink, 0, 1)).toFixed(2));
      }
    }

    satTargets(def, t);
    const swarmOn = def.mode === 'swarm';
    sats.forEach(s => {
      s.el.setAttribute(
        'transform',
        `translate(${s.x.x.toFixed(2)} ${s.y.x.toFixed(2)}) rotate(${s.r.x.toFixed(1)}) scale(${Math.max(s.s.x, 0.001).toFixed(4)} ${Math.max(s.sy.x, 0.001).toFixed(4)})`,
      );
      s.el.setAttribute('opacity', clamp(s.op.x, 0, 1).toFixed(3));
      s.eyes.style.opacity = swarmOn ? '1' : '0';
      if (swarmOn)
        s.eyes.setAttribute('transform', `scale(1 ${clamp(s.bl.x, 0.08, 1).toFixed(3)})`);
      s.dollar.style.opacity = def.mode === 'coin' && s.i === 0 ? '1' : '0';
    });

    scheduleFrame();
  }
  scheduleFrame();

  return {
    setState,
    getState: () => state,
    spin: () => {
      spinDeg.t += 360;
    },
    burst,
    hop: () => {
      scaleY.v += 5;
      scaleX.v -= 3;
      if (!STATES[state].hop) {
        poseFrom = currentPose();
        poseTo = POSES.happy;
        poseT.x = 0;
        poseT.t = 1;
        nextPoseAt = now + 1200;
      }
    },
    setPointer: (x: number, y: number) => {
      pointer = { x, y };
    },
    setPaused: (value: boolean) => {
      if (destroyed || value === paused) return;
      paused = value;
      ownerWindow.cancelAnimationFrame(rafId);
      ownerWindow.clearTimeout(frameTimer);
      if (!paused) {
        last = performance.now();
        acc = 0;
        scheduleFrame();
      }
    },
    destroy: () => {
      destroyed = true;
      ownerWindow.cancelAnimationFrame(rafId);
      ownerWindow.clearTimeout(frameTimer);
      timeouts.forEach(id => clearTimeout(id));
      timeouts.clear();
      svg.replaceChildren();
    },
  };
}
